import React from "react"
import PropTypes from "prop-types"
import clsx from "clsx"
import styles from "./button.module.css"

/**
 * @example
 * ```
  // variants
  <Button>base</Button>
  <Button variant="primary">primary</Button>
  <Button variant="secondary">secondary</Button>
  <Button variant="success">success</Button>
  <Button variant="error">error</Button>
  <Button variant="info">info</Button>
  <Button variant="warning">warning</Button>

  // sizes
  <Button variant="info" size="sm">small</Button>
  <Button variant="info">normal</Button>
  <Button variant="info" size="lg">large</Button>
  
  // sizes with icon only
  <Button variant="info" size="icon-sm"><FaSearch /></Button>
  <Button variant="info" size="icon"><FaSearch /></Button>
  <Button variant="info" size="icon-lg"><FaSearch /></Button>
 * ```
 */

const BUTTON_VARIANTS = ["default", "primary", "secondary", "error", "success", "info", "warning"]
const BUTTON_SIZES = ["sm", "md", "lg", "icon", "icon-sm", "icon-lg"]

/**
 * @typedef {Object} ButtonBaseProps
 * @property {"default" | "primary" | "secondary" | "error" | "success" | "info" | "warning"} variant
 * @property {"sm" | "md" | "lg" | "icon" | "icon-sm" | "icon-lg"} size
 */

/**
 * @typedef {React.ButtonHTMLAttributes<HTMLButtonElement> & ButtonBaseProps} ButtonProps
 */

/**
 * @type {React.ForwardRefExoticComponent<ButtonProps & React.RefAttributes<HTMLButtonElement>>}
 */
export const Button = React.forwardRef((props, ref) => {
  const {
    type = "button",
    variant = "default",
    size = "md",
    className,
    children,
    ...rest
  } = props

  return (
    <button
      ref={ref}
      type={type}
      className={clsx(
        styles.base,
        variant !== "default" && styles[variant],
        size !== "md" && styles[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
})

Button.propTypes = {
  variant: PropTypes.oneOf(BUTTON_VARIANTS),
  size: PropTypes.oneOf(BUTTON_SIZES),
}
